// SelectorGeografia — combos desplegables en cascada País → Departamento →
// Municipio, alimentados SIEMPRE por el catálogo de geografía del
// ecosistema (GeografiaController → tablas de geografía de Ecolab). Toda
// selección de país / departamento / municipio en CoopNet debe usar este
// componente: nunca texto libre.
//
// `nivel` dice hasta dónde se selecciona ('pais' | 'departamento' |
// 'municipio'). Si un país todavía no tiene departamentos (o un departamento
// no tiene municipios) en el catálogo, ese nivel se considera resuelto para
// no dejar al usuario bloqueado.
//
// Uso:
//   <SelectorGeografia
//     nivel="municipio"
//     valor={{ paisId, departamentoId, municipioId }}
//     onChange={(g) => ...}          // g = { paisId, departamentoId, municipioId,
//                                    //       paisNombre, departamentoNombre, municipioNombre }
//     onCompletoChange={setCompleto} // true cuando ya eligió lo que `nivel` exige
//   />

import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { geografiaApi } from '../services/coopnetApi';

const lista = (d) => (Array.isArray(d) ? d : []);
const num = (x) => (x === '' || x === null || x === undefined ? '' : Number(x));
const nombreDe = (items, id) => items.find((i) => String(i.id) === String(id))?.nombre || '';
const existe = (items, id) => id !== '' && items.some((i) => String(i.id) === String(id));

/**
 * @param {'pais'|'departamento'|'municipio'} nivel
 * @param {{paisId?: number|string, departamentoId?: number|string, municipioId?: number|string}} valor
 * @param {(g: object) => void} onChange
 * @param {(completo: boolean) => void} [onCompletoChange]
 * @param {{pais?: string, departamento?: string, municipio?: string}} [etiquetas]
 * @param {boolean} [todosLosPaises] pide el catálogo completo de países (por defecto, el habitual del ecosistema)
 * @param {boolean} [disabled]
 */
export default function SelectorGeografia({
  nivel = 'municipio',
  valor = {},
  onChange,
  onCompletoChange,
  etiquetas = {},
  todosLosPaises = false,
  disabled = false,
}) {
  const paisId = num(valor.paisId);
  const departamentoId = num(valor.departamentoId);
  const municipioId = num(valor.municipioId);

  const conDepartamento = nivel === 'departamento' || nivel === 'municipio';
  const conMunicipio = nivel === 'municipio';

  const [paises, setPaises] = useState([]);
  const [deptos, setDeptos] = useState([]);
  const [munis, setMunis] = useState([]);
  const [cargando, setCargando] = useState({ paises: true, deptos: false, munis: false });
  const [falloCarga, setFalloCarga] = useState(false);

  // Países. Si el país ya elegido no está en el listado habitual (p. ej. un
  // registro antiguo), se pide el catálogo completo para poder mostrarlo.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        let p = lista(await geografiaApi.paises(todosLosPaises));
        if (!todosLosPaises && paisId !== '' && !existe(p, paisId)) p = lista(await geografiaApi.paises(true));
        if (vivo) {
          setPaises(p);
          setFalloCarga(false);
        }
      } catch {
        if (vivo) setFalloCarga(true);
      } finally {
        if (vivo) setCargando((c) => ({ ...c, paises: false }));
      }
    })();
    return () => {
      vivo = false;
    };
    // paisId a propósito fuera de las dependencias: solo importa al cargar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todosLosPaises]);

  // Departamentos del país elegido.
  useEffect(() => {
    if (!conDepartamento || paisId === '') {
      setDeptos([]);
      return undefined;
    }
    let vivo = true;
    setCargando((c) => ({ ...c, deptos: true }));
    geografiaApi
      .departamentos(paisId)
      .then((d) => vivo && setDeptos(lista(d)))
      .catch(() => vivo && setDeptos([]))
      .finally(() => vivo && setCargando((c) => ({ ...c, deptos: false })));
    return () => {
      vivo = false;
    };
  }, [conDepartamento, paisId]);

  // Municipios del departamento elegido.
  useEffect(() => {
    if (!conMunicipio || departamentoId === '') {
      setMunis([]);
      return undefined;
    }
    let vivo = true;
    setCargando((c) => ({ ...c, munis: true }));
    geografiaApi
      .municipios(departamentoId)
      .then((m) => vivo && setMunis(lista(m)))
      .catch(() => vivo && setMunis([]))
      .finally(() => vivo && setCargando((c) => ({ ...c, munis: false })));
    return () => {
      vivo = false;
    };
  }, [conMunicipio, departamentoId]);

  const completo = useMemo(() => {
    if (paisId === '') return false;
    if (nivel === 'pais') return true;
    if (cargando.deptos) return false;
    if (deptos.length === 0) return true; // el país no tiene departamentos en el catálogo
    if (departamentoId === '') return false;
    if (nivel === 'departamento') return true;
    if (cargando.munis) return false;
    if (munis.length === 0) return true; // el departamento no tiene municipios en el catálogo
    return municipioId !== '';
  }, [nivel, paisId, departamentoId, municipioId, deptos, munis, cargando]);

  useEffect(() => {
    onCompletoChange?.(completo);
  }, [completo, onCompletoChange]);

  const emitir = (parcial) => {
    const g = { paisId, departamentoId, municipioId, ...parcial };
    onChange?.({
      ...g,
      paisNombre: nombreDe(paises, g.paisId),
      departamentoNombre: g.departamentoId === '' ? '' : nombreDe(deptos, g.departamentoId),
      municipioNombre: g.municipioId === '' ? '' : nombreDe(munis, g.municipioId),
    });
  };

  const columnas = nivel === 'pais' ? 1 : nivel === 'departamento' ? 2 : 3;
  const fin = (cargandoNivel) => (cargandoNivel ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${columnas}, 1fr)` }, gap: 2 }}>
      <FormControl fullWidth disabled={disabled || cargando.paises} error={falloCarga}>
        <InputLabel>{etiquetas.pais || 'País'}</InputLabel>
        <Select
          label={etiquetas.pais || 'País'}
          value={existe(paises, paisId) ? paisId : ''}
          endAdornment={fin(cargando.paises)}
          onChange={(e) => emitir({ paisId: num(e.target.value), departamentoId: '', municipioId: '' })}
        >
          {paises.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.nombre}
            </MenuItem>
          ))}
        </Select>
        {falloCarga && <FormHelperText>No se pudo cargar el catálogo de países. Recarga la página.</FormHelperText>}
      </FormControl>

      {conDepartamento && (
        <FormControl fullWidth disabled={disabled || paisId === '' || cargando.deptos || deptos.length === 0}>
          <InputLabel>{etiquetas.departamento || 'Departamento'}</InputLabel>
          <Select
            label={etiquetas.departamento || 'Departamento'}
            value={existe(deptos, departamentoId) ? departamentoId : ''}
            endAdornment={fin(cargando.deptos)}
            onChange={(e) => emitir({ departamentoId: num(e.target.value), municipioId: '' })}
          >
            {deptos.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.nombre}
              </MenuItem>
            ))}
          </Select>
          {paisId !== '' && !cargando.deptos && deptos.length === 0 && (
            <FormHelperText>Este país aún no tiene departamentos en el catálogo.</FormHelperText>
          )}
        </FormControl>
      )}

      {conMunicipio && (
        <FormControl fullWidth disabled={disabled || departamentoId === '' || cargando.munis || munis.length === 0}>
          <InputLabel>{etiquetas.municipio || 'Municipio'}</InputLabel>
          <Select
            label={etiquetas.municipio || 'Municipio'}
            value={existe(munis, municipioId) ? municipioId : ''}
            endAdornment={fin(cargando.munis)}
            onChange={(e) => emitir({ municipioId: num(e.target.value) })}
          >
            {munis.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
}
